import React, { useEffect, useMemo, useState } from "react";

const COLOR_OPTIONS = [
  "black",
  "white",
  "gray",
  "brown",
  "beige",
  "blue",
  "red",
  "green",
  "yellow",
  "pink",
  "purple",
  "orange",
  "olive",
  "khaki",
  "cream",
];

const SIZE_OPTIONS = ["S", "M", "L", "XL", "XXL", "XXXL"];

const emptyVariant = {
  color: "black",
  size: "S",
  stock: 0,
  sku: "",
  priceOverride: "",
};

const VariantModal = ({
  isOpen,
  onClose,
  onSave,
  existingVariants,
  editingIndex,
  basePrice,
  initialVariant,
}) => {
  const [draft, setDraft] = useState(initialVariant || emptyVariant);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setDraft(initialVariant || emptyVariant);
      setShowAdvanced(
        Boolean(initialVariant?.sku || initialVariant?.priceOverride),
      );
    }
  }, [isOpen, initialVariant]);

  const duplicateVariant = useMemo(() => {
    return existingVariants.some((variant, index) => {
      if (editingIndex !== null && index === editingIndex) return false;
      return variant.color === draft.color && variant.size === draft.size;
    });
  }, [draft.color, draft.size, editingIndex, existingVariants]);

  const isStockValid =
    Number.isInteger(Number(draft.stock)) && Number(draft.stock) >= 0;

  if (!isOpen) return null;

  const handleSave = () => {
    if (!draft.color || !draft.size || !isStockValid) return;

    const nextVariant = {
      ...draft,
      stock: Number(draft.stock),
      sku: draft.sku || "",
    };

    if (
      nextVariant.priceOverride === "" ||
      nextVariant.priceOverride === null ||
      Number.isNaN(Number(nextVariant.priceOverride))
    ) {
      delete nextVariant.priceOverride;
    } else {
      nextVariant.priceOverride = Number(nextVariant.priceOverride);
    }

    onSave(nextVariant);
    onClose();
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1200,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "min(560px, 92vw)",
          background: "#fff",
          borderRadius: 14,
          padding: 20,
          boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <h3 style={{ margin: 0 }}>
            {editingIndex !== null ? "Edit variant" : "Add variant"}
          </h3>
          <button type="button" onClick={onClose}>
            Close
          </button>
        </div>

        <div style={{ display: "grid", gap: 16 }}>
          <div>
            <label style={{ display: "block", marginBottom: 8 }}>Color</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {COLOR_OPTIONS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setDraft((prev) => ({ ...prev, color }))}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    border:
                      draft.color === color
                        ? "2px solid #f97316"
                        : "2px solid #d1d5db",
                    background: color === "white" ? "#fff" : color,
                    boxShadow:
                      draft.color === color
                        ? "0 0 0 2px rgba(249,115,22,0.2)"
                        : "none",
                  }}
                  aria-label={color}
                />
              ))}
            </div>
          </div>

          <div>
            <label style={{ display: "block", marginBottom: 8 }}>Size</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {SIZE_OPTIONS.map((size) => (
                <button
                  key={size}
                  type="button"
                  onClick={() => setDraft((prev) => ({ ...prev, size }))}
                  style={{
                    border:
                      draft.size === size
                        ? "1px solid #f97316"
                        : "1px solid #d1d5db",
                    background: draft.size === size ? "#fff7ed" : "#fff",
                    borderRadius: 8,
                    padding: "6px 10px",
                    cursor: "pointer",
                    fontWeight: draft.size === size ? 700 : 500,
                  }}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label style={{ display: "block", marginBottom: 8 }}>Stock</label>
            <input
              type="number"
              min="0"
              step="1"
              value={draft.stock}
              onChange={(e) =>
                setDraft((prev) => ({
                  ...prev,
                  stock: e.target.value,
                }))
              }
              style={{
                width: "100%",
                padding: 10,
                borderRadius: 8,
                border: "1px solid #d1d5db",
              }}
            />
          </div>

          <div>
            <button
              type="button"
              onClick={() => setShowAdvanced((prev) => !prev)}
              style={{
                border: "1px solid #d1d5db",
                borderRadius: 8,
                background: "#fff",
                padding: "8px 12px",
                cursor: "pointer",
              }}
            >
              {showAdvanced ? "Hide advanced" : "Advanced"}
            </button>
          </div>

          {showAdvanced && (
            <div style={{ display: "grid", gap: 12 }}>
              {/* <div>
                <label style={{ display: "block", marginBottom: 8 }}>SKU</label>
                <input
                  type="text"
                  value={draft.sku}
                  onChange={(e) =>
                    setDraft((prev) => ({ ...prev, sku: e.target.value }))
                  }
                  style={{
                    width: "100%",
                    padding: 10,
                    borderRadius: 8,
                    border: "1px solid #d1d5db",
                  }}
                />
              </div> */}

              <div>
                <label style={{ display: "block", marginBottom: 8 }}>
                  Price override (optional)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder={basePrice ? String(basePrice) : "Base price"}
                  value={draft.priceOverride}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      priceOverride: e.target.value,
                    }))
                  }
                  style={{
                    width: "100%",
                    padding: 10,
                    borderRadius: 8,
                    border: "1px solid #d1d5db",
                  }}
                />
              </div>
            </div>
          )}

          {duplicateVariant && (
            <div style={{ color: "#b91c1c", fontSize: 13 }}>
              A variant with this color and size already exists. Edit it instead
              or choose a different combination.
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 20,
          }}
        >
          <button type="button" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={
              !draft.color || !draft.size || !isStockValid || duplicateVariant
            }
            style={{
              opacity:
                !draft.color || !draft.size || !isStockValid || duplicateVariant
                  ? 0.5
                  : 1,
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default VariantModal;
